import { useState } from 'react';
import { daysBetween, fmtD, invoiceRows, money, nowYM, plural, today0, ymAdd, type Deadline } from '@biuro/shared';
import { alerts } from '../alerts.ts';
import { closeStatus, defaultCloseMonth, monthGen, monthLower } from '../close.ts';
import { CostChart, Kpi, MonthCalendar } from '../dash.tsx';
import { useData, useMe, useNav, useSettings } from '../hooks.tsx';
import { Icon, shortLabel, todayLong } from '../ui.tsx';
import { DeadlineList, deadlines } from './common.tsx';

const STEP_ICON = { ok: Icon.check, todo: Icon.clock, late: Icon.alert, none: Icon.clock } as const;

function CloseCard() {
  const { data } = useData();
  const st = useSettings();
  const { go } = useNav();
  const m = defaultCloseMonth();
  const s = closeStatus(data, st, m);
  const done = s.steps.filter((x) => x.state === 'ok').length;
  return (
    <>
      <div className="card-head">
        <div><h2>Zamknięcie {monthGen(m)}</h2><p>{s.docsDue ? `Paczka dla biura do ${fmtD(s.docsDue)}` : 'Dokumenty, faktury i wysyłka do biura'}</p></div>
        <span className="muted small">{done} z 3</span>
      </div>
      <div className="meter ok" style={{ marginBottom: 12 }}><i style={{ width: `${(done / 3) * 100}%` }} /></div>
      <ol className="steps">
        {s.steps.map((x) => {
          const I = STEP_ICON[x.state];
          return (
            <li key={x.key} className={x.state}>
              <span className="ic"><I /></span>
              <span>{x.label}</span>
              <span className="st">{x.text}</span>
            </li>
          );
        })}
      </ol>
      <div className="actions" style={{ marginTop: 14 }}><button className="btn" onClick={() => go('miesiac')}>Przejdź do zamknięcia</button></div>
    </>
  );
}

export function Pulpit() {
  const { data, isLoading } = useData();
  const me = useMe().data;
  const st = useSettings();
  const { go } = useNav();
  const t = today0(), cur = nowYM();
  const [calYM, setCalYM] = useState(cur);
  const [allAlerts, setAllAlerts] = useState(false);
  const list = deadlines(st, data, ymAdd(cur, -3), ymAdd(cur, 6));
  const isDone = (d: Deadline) => !!data.done[d.id] || d.before;
  const up = list.filter((d) => !isDone(d) && d.due >= t);
  const al = isLoading ? [] : alerts(data, st, me);
  const calList = deadlines(st, data, calYM, calYM);

  // kafle
  const nx = up[0];
  const n = nx ? daysBetween(t, nx.due) : null;
  const m = defaultCloseMonth();
  const s = closeStatus(data, st, m);
  const cost = s.rows.reduce((a, x) => a + (x.c.net || 0), 0);
  const unsent = invoiceRows(data).filter((x) => !x.c.draft && (x.inv.status || 'new') === 'new' && x.c.month && x.c.month <= m).length;
  const docsLate = !s.sent && s.docsDue && t > s.docsDue;
  const inf = s.active.length;

  return (
    <>
      <div className="vh">
        <div>
          <h1>Pulpit</h1>
          <p>{todayLong()}</p>
        </div>
        <div className="actions">
          <button className="btn" onClick={() => go('miesiac')}><Icon.download />Paczka dla biura</button>
          <button className="btn pri" onClick={() => go('faktury', { sub: 'new' })}><Icon.plus />Dodaj fakturę</button>
        </div>
      </div>

      <div className="dash">
        <div className="w12 kpis">
          <Kpi label="Najbliższy termin" icon={<Icon.clock />} focus={n != null && n <= 3} onClick={() => go('terminy')}
            value={n == null ? '—' : n === 0 ? 'Dziś' : n === 1 ? 'Jutro' : n} unit={n != null && n > 1 ? 'dni' : undefined}
            sub={nx ? `${shortLabel(nx)}, ${fmtD(nx.due)}` : 'brak otwartych terminów'} />
          <Kpi label="Do wysłania do biura" icon={<Icon.send />} onClick={() => go('miesiac')}
            value={unsent} unit={plural(unsent, 'faktura', 'faktury', 'faktur')}
            sub={s.sent ? `${monthLower(m)} wysłany` : s.docsDue ? (docsLate ? `termin ${fmtD(s.docsDue)} minął` : `termin ${fmtD(s.docsDue)}`) : ''} subTone={docsLate ? 'bad' : ''} />
          <Kpi label="Koszty usług zagranicznych" icon={<Icon.coins />} onClick={() => go('faktury')}
            value={money(cost).replace(' zł', '')} unit="zł" sub={`${monthLower(m)}, netto`} />
          <Kpi label="Dokumenty w inFakcie" icon={<Icon.doc />} onClick={() => go('miesiac')}
            value={inf ? `${s.infaktOk} z ${inf}` : '—'} sub={inf ? `za ${monthLower(m)}${s.steps[0].state === 'late' ? ', po terminie' : ''}` : 'dodaj szablony'}
            subTone={s.steps[0].state === 'late' ? 'bad' : ''}>
            {inf > 0 && <span className={`meter ${s.infaktOk === inf ? 'ok' : ''}`}><i style={{ width: `${(s.infaktOk / inf) * 100}%` }} /></span>}
          </Kpi>
        </div>

        <div className="col-main">
        <section className="card"><CostChart data={data} /></section>

        <section className="card">
          <MonthCalendar ym={calYM} list={calList} isDone={isDone} nav={(
            <div className="cal-nav">
              <button className="btn sm" aria-label="Poprzedni miesiąc" onClick={() => setCalYM(ymAdd(calYM, -1))}><Icon.prev /></button>
              {calYM !== cur && <button className="btn sm" onClick={() => setCalYM(cur)}>Dziś</button>}
              <button className="btn sm" aria-label="Następny miesiąc" onClick={() => setCalYM(ymAdd(calYM, 1))}><Icon.next /></button>
            </div>
          )} />
          <div className="cal-legend"><span><i />termin</span><span><i className="late" />po terminie</span><span><i className="done" />zrobione</span></div>
        </section>
        </div>

        <div className="col-side side-col">
          <section className="card close-card"><CloseCard /></section>
          {al.length > 0 && (
            <section className="card compact">
              <div className="card-head"><h2>Do zrobienia</h2><span className="muted small">{al.length}</span></div>
              {(allAlerts ? al : al.slice(0, 3)).map((a, i) => (
                <div className={`al ${a.lvl}`} key={i}>
                  <div className="al-b"><b>{a.title}</b><div>{a.text}</div></div>
                  {a.action && (a.action.href
                    ? <a className="btn sm" href={a.action.href}>{a.action.label}</a>
                    : <button className="btn sm" onClick={() => go(a.action!.tab, { sub: a.action!.sub })}>{a.action.label}</button>)}
                </div>
              ))}
              {al.length > 3 && <div className="add-row"><button className="btn ghost sm" onClick={() => setAllAlerts((v) => !v)}>{allAlerts ? 'Pokaż mniej' : `Pokaż wszystkie (${al.length})`}</button></div>}
            </section>
          )}
          <section className="card">
            <div className="card-head"><h2>Najbliższe terminy</h2><button className="btn ghost sm" onClick={() => go('terminy')}>Wszystkie</button></div>
            {up.length ? <DeadlineList list={up.slice(0, 4)} data={data} /> : <div className="empty">Brak otwartych terminów.</div>}
          </section>
        </div>
      </div>
    </>
  );
}
